import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Typography } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import {
  GridCellProps,
  GridCustomCellProps,
  GridCustomHeaderCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  Grid as KendoGrid,
  GridColumn as Column,
} from "@progress/kendo-react-grid";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import IconButton from "../../components/common/IconButton";
import { MdDelete } from "react-icons/md";
import { LoadingPanel } from "../../components/layout/Loading";
import { setBookingID } from "./bookingSlice";
import { FiEdit } from "react-icons/fi";
import { checkAcessRights, formatIndianNumber } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  EXCEL_EXPORT_ACCESS,
} from "../../_contstants/common";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import { getAllBookings } from "./services/booking.services";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import moment from "moment";
import { Button } from "@progress/kendo-react-buttons";

const Booking: React.FC = () => {
  const isExcelExportAccess = checkAcessRights(
    location.pathname,
    EXCEL_EXPORT_ACCESS
  );
  const _export = React.useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };
  return (
    <>
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Booking List
          </Typography.h4>
          {isExcelExportAccess && (
            <Button
              title="Export Excel"
              themeColor={"primary"}
              type="button"
              onClick={excelExport}
            >
              Export to Excel
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <BookingGridComponent ref={_export} />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const BookingGridComponent = ({ ref }: { ref: any }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);

  const BookingList = useAppSelector((state) => state.booking.BookingList);
  const loading = useAppSelector((state) => state.booking.loading);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  useEffect(() => {
    const payload = {
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };
    dispatch(getAllBookings(payload));
  }, [currentPage, pageLimit]);

  useEffect(() => {
    return () => {
      dispatch(closeDialog());
      dispatch(setCurrentPage(0));
    };
  }, []);

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEditBooking = (ID: number) => {
      navigate("/booking/edit", {
        state: { BookingID: ID },
      });
    };

    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteBooking"));
      dispatch(setBookingID(ID));
    };

    return (
      <td className={`action-td ${props.className}`} style={props.style}>
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => handleEditBooking(props.dataItem.ID)}
            icon={FiEdit}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "20px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };
  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 9 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );
  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };
  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const gridRef = useRef<any>(null);
  // if (loading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <ExcelExport
        data={BookingList}
        ref={ref}
        fileName={`Booking_list_${moment().format("DD-MM-YYYY hh-mm A")}.xlsx`}
      >
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          className="responsive-table"
          filterable={true}
          filter={filter}
          data={filter ? filterBy(BookingList, filter) : BookingList}
          onFilterChange={handleFilterChange}
          skip={currentPage * pageLimit}
          take={pageLimit}
          total={BookingList[0]?.ItemCount || BookingList?.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [10, 25, 50, 100, 500],
            pageSizeValue: pageLimit,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column field="BookingNo" title="Booking No." width={200} />
          <Column field="TravelDate" title="Travel Date" width={150} />
          <Column
            field="Name"
            title="Customer Name"
            width={150}
            cells={{
              data: (props: any) => (
                <td>
                  {props.dataItem?.FirstName} {props.dataItem?.LastName}
                </td>
              ),
            }}
          />
          <Column field="MobileNo" title="Mobile No." width={150} />
          <Column field="EmailID" title="Email ID" width={250} />
          <Column field="TourName" title="Tour" width={200} />
          <Column field="SectorName" title="Sector" width={150} />
          <Column
            field="NoOfNights"
            title="Nights"
            width={120}
            cells={{
              data: (props: any) => (
                <td className="text-center">{+props.dataItem?.NoOfNights}</td>
              ),
            }}
          />
          <Column
            field="TotalPax"
            title="Total Pax"
            width={120}
            cells={{
              data: (props: any) => (
                <td className="text-center">{+props.dataItem?.TotalPax}</td>
              ),
            }}
          />
          <Column
            field="TotalAmt"
            title="Total Amount"
            width={150}
            cells={{
              data: (props: any) => (
                <td
                  {...props.tdProps}
                  className={`${props.className} text-end`}
                  style={props.tdProps.style}
                >
                  {formatIndianNumber(+props.dataItem?.TotalAmt || 0)}
                </td>
              ),
            }}
            locked={true}
          />
          {false && (isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              cells={{ data: MyEditCommandCell }}
              width={"110px"}
              filterable={false}
              locked={true}
            />
          )}
        </KendoGrid>
      </ExcelExport>
    </>
  );
};

export default Booking;
